/**
 * Sprint 3.6 — Tabela detalhada de status OTA por device.
 *
 * Aparece na página Firmware (abaixo das releases). Mostra a tabela
 * derivada de /api/admin/fleet/ota-status com filtros:
 *   - fw_version
 *   - only_failed
 *
 * Decisões:
 *   - Filtros são controles inline acima da tabela
 *   - Refresh manual via botão (recente o suficiente vs custo de
 *     polling automático constante na página de releases)
 *   - Linha clicada poderia abrir DeviceDetail no futuro — Sprint 3.6
 *     deixa só como visualização, sem navegação adicional
 */
import { useCallback, useEffect, useState } from "react";

import { getFleetOtaStatus } from "../lib/api.js";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function OtaStatusTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filtros
  const [fwVersion, setFwVersion] = useState("");
  const [onlyFailed, setOnlyFailed] = useState(false);

  const fetch_ = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        setError(null);
        // string vazia significa "todos" — não envia o filtro
        const opts = { only_failed: onlyFailed, signal };
        if (fwVersion.trim() !== "") {
          opts.fw_version = fwVersion.trim();
        }
        const data = await getFleetOtaStatus(opts);
        setRows(data?.devices || []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Erro ao carregar OTA status");
      } finally {
        setLoading(false);
      }
    },
    [fwVersion, onlyFailed]
  );

  useEffect(() => {
    const ctl = new AbortController();
    fetch_(ctl.signal);
    return () => ctl.abort();
  }, [fetch_]);

  return (
    <section className="card-pad space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          OTA status ({rows.length})
        </h2>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <input
            type="text"
            placeholder="fw_version filter"
            value={fwVersion}
            onChange={(e) => setFwVersion(e.target.value)}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs font-mono w-32"
          />
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={onlyFailed}
              onChange={(e) => setOnlyFailed(e.target.checked)}
            />
            only failed
          </label>
          <button
            type="button"
            onClick={() => fetch_()}
            disabled={loading}
            className="text-xs px-2 py-1 rounded border border-noc-muted/40 hover:border-noc-text/60 disabled:opacity-50"
          >
            🔄
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 break-words">
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="text-sm text-noc-muted">Carregando...</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-noc-muted">
          Nenhum device com o filtro atual.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint border-b border-noc-border">
                <th className="py-2 pr-3">Device</th>
                <th className="py-2 pr-3">FW</th>
                <th className="py-2 pr-3">Online</th>
                <th className="py-2 pr-3">Secure</th>
                <th className="py-2 pr-3">OTA state</th>
                <th className="py-2 pr-3 text-right">Progress</th>
                <th className="py-2 pr-3">Pending</th>
                <th className="py-2 pr-3">Last ack</th>
                <th className="py-2 pr-3">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.device_id}
                  className="border-b border-noc-border/30 hover:bg-noc-bg/40"
                >
                  <td className="py-2 pr-3 font-mono text-xs">
                    {d.device_id}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">
                    {d.fw_version || (
                      <span className="font-mono text-[12px] text-noc-faint">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={d.online ? "text-noc-ok" : "text-noc-warn"}>
                      {d.online ? "●" : "○"}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    <span className={d.secure_channel ? "text-noc-ok" : "text-noc-muted"}>
                      {d.secure_channel ? "✓" : "—"}
                    </span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">
                    <OtaStateBadge state={d.ota_state} />
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-right">
                    {typeof d.ota_progress_pct === "number" ? `${d.ota_progress_pct}%` : "—"}
                  </td>
                  <td className="py-2 pr-3">
                    {d.has_pending_ota ? (
                      <span className="text-noc-warn">●</span>
                    ) : (
                      <span className="text-noc-muted">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {d.last_ota_ack_success === true ? (
                      <span className="text-noc-ok">success</span>
                    ) : d.last_ota_ack_success === false ? (
                      <span
                        className="text-noc-err cursor-help"
                        title={d.last_ota_ack_message || ""}
                      >
                        failed
                        {d.last_ota_ack_message && (
                          <span className="text-noc-muted">
                            : {d.last_ota_ack_message.slice(0, 24)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-noc-muted">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-noc-muted">
                    {formatDate(d.last_seen)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OtaStateBadge({ state }) {
  if (!state) return <span className="text-noc-muted">—</span>;
  const color =
    state === "idle"
      ? "text-noc-muted"
      : state === "failed"
      ? "text-noc-err"
      : state === "downloading" || state === "verifying" || state === "flashing"
      ? "text-noc-warn"
      : state === "rebooting"
      ? "text-noc-warn"
      : "text-noc-text";
  return <span className={color}>{state}</span>;
}
